import { useMemo, useState } from 'react';
import { Camera, Keyboard, Mic, RotateCcw } from 'lucide-react';
import type { Capture, Formulation, Instrument, Source } from '../lib/types';
import { formatDateTime } from '../lib/format';
import { Badge, Button, EmptyState, Modal, cx } from './ui';
import type { ConfirmationSource } from './ConfirmationScreen';

const MODE_ICON: Record<Source, typeof Camera> = {
  photo: Camera,
  voice: Mic,
  manual: Keyboard,
};

const INSTRUMENT_LABEL: Record<string, string> = {
  zetasizer: 'Zetasizer',
  ribogreen: 'Encapsulation Data',
  unknown: 'Unknown instrument',
};

/**
 * The two instruments are run on different machines and never share a source,
 * so in a project using both this list is two interleaved streams. Giving each
 * its own colour makes that legible at a glance, and the filter above makes it
 * possible to audit one stream without reading past the other.
 */
const INSTRUMENT_TONE: Record<string, 'info' | 'good' | 'neutral'> = {
  zetasizer: 'info',
  ribogreen: 'good',
  unknown: 'neutral',
};

const STATUS_TONE: Record<Capture['status'], 'good' | 'warn' | 'bad' | 'neutral' | 'info'> = {
  confirmed: 'good',
  extracted: 'warn',
  pending: 'info',
  rejected: 'neutral',
  failed: 'bad',
};

const STATUS_LABEL: Record<Capture['status'], string> = {
  confirmed: 'confirmed',
  extracted: 'awaiting confirmation',
  pending: 'pending',
  rejected: 'rejected',
  failed: 'failed',
};

export default function CaptureHistory({
  open,
  onClose,
  captures,
  formulations,
  readOnly,
  onReopen,
}: {
  open: boolean;
  onClose: () => void;
  captures: Capture[];
  formulations: Formulation[];
  readOnly: boolean;
  onReopen: (source: ConfirmationSource) => void;
}) {
  const labelById = new Map(formulations.map((f) => [f.id, f.label || 'Untitled']));
  const [instrumentFilter, setInstrumentFilter] = useState<Instrument | 'all'>('all');

  /**
   * Only worth showing when the project actually uses more than one instrument —
   * a DLS-only project would just get a row of chrome with nothing to filter.
   */
  const instrumentCounts = useMemo(() => {
    const counts = new Map<Instrument, number>();
    for (const c of captures) counts.set(c.instrument, (counts.get(c.instrument) ?? 0) + 1);
    return counts;
  }, [captures]);

  const visible = useMemo(
    () =>
      instrumentFilter === 'all'
        ? captures
        : captures.filter((c) => c.instrument === instrumentFilter),
    [captures, instrumentFilter],
  );

  /** Reopening a capture puts the original photo or transcript back on screen. */
  function reopen(c: Capture) {
    onReopen({
      mode: c.mode,
      captureId: c.id,
      instrument: c.instrument,
      extraction: c.rawExtraction ?? null,
      transcript: c.transcript,
      previewUrl: c.downloadUrl,
      storagePath: c.storagePath,
      targetFormulationId: c.appliedTo?.[0] ?? null,
      focusKey: null,
      error: c.status === 'failed' ? (c.errorMessage ?? 'Extraction failed.') : null,
    });
    onClose();
  }

  return (
    <Modal open={open} onClose={onClose} title="Capture history" wide>
      <p className="mb-4 text-sm text-ink-400">
        Every number traces back to one of these. Photos and transcripts are kept so a value can be
        checked months later.
        {' '}
        {/* Which build is actually running. The app is a WebView onto the
            deployed site and caches, so without this there is no way to tell a
            fix that failed from a fix that never arrived on the device. */}
        <span className="text-ink-600">Build {__BUILD_STAMP__}</span>
      </p>

      {instrumentCounts.size > 1 && (
        <div className="mb-3 flex flex-wrap gap-1 rounded-lg bg-ink-800 p-1">
          {([['all', 'All'] as const] as [Instrument | 'all', string][])
            .concat(
              [...instrumentCounts.keys()].map((k) => [k, INSTRUMENT_LABEL[k] ?? k] as [Instrument, string]),
            )
            .map(([key, label]) => {
              const count = key === 'all' ? captures.length : (instrumentCounts.get(key) ?? 0);
              return (
                <button
                  key={key}
                  onClick={() => setInstrumentFilter(key)}
                  className={cx(
                    'rounded-md px-3 py-1 text-xs font-medium transition-colors',
                    'inline-flex items-center gap-1.5 pointer-coarse:min-h-11 pointer-coarse:px-4',
                    instrumentFilter === key
                      ? 'bg-accent-500 text-white'
                      : 'text-ink-400 hover:text-ink-100',
                  )}
                >
                  {label}
                  <span className="tabular-nums opacity-70">{count}</span>
                </button>
              );
            })}
        </div>
      )}

      {captures.length === 0 ? (
        <EmptyState title="Nothing captured yet" body="Photo, voice and manual entries all appear here." />
      ) : (
        <ul className="space-y-2">
          {visible.map((c) => {
            const Icon = MODE_ICON[c.mode];
            const unreadable = c.rawExtraction?.unreadableFields ?? [];
            return (
              <li
                key={c.id}
                className={cx(
                  'rounded-lg border p-3',
                  c.status === 'extracted' ? 'border-amber-800 bg-amber-950/20' : 'border-ink-700 bg-ink-800/40',
                )}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Icon size={14} className="text-ink-400" />
                  <span className="text-sm font-medium capitalize">{c.mode}</span>
                  <Badge tone={INSTRUMENT_TONE[c.instrument] ?? 'neutral'}>
                    {INSTRUMENT_LABEL[c.instrument] ?? c.instrument}
                  </Badge>
                  <Badge tone={STATUS_TONE[c.status]}>{STATUS_LABEL[c.status]}</Badge>
                  {c.rawExtraction?.confidence && (
                    <Badge tone={c.rawExtraction.confidence === 'low' ? 'bad' : 'neutral'}>
                      {c.rawExtraction.confidence}
                    </Badge>
                  )}
                  <span className="ml-auto text-xs text-ink-400">
                    {c.capturedByName ? `${c.capturedByName} · ` : ''}
                    {formatDateTime(c.createdAt)}
                  </span>
                </div>

                {c.transcript && (
                  <p className="mt-2 rounded-md bg-ink-900 p-2 font-mono text-xs text-ink-300">
                    {c.transcript}
                  </p>
                )}

                {(c.appliedTo?.length ?? 0) > 0 && (
                  <p className="mt-2 text-xs text-ink-400">
                    Applied to {c.appliedTo.map((id) => labelById.get(id) ?? id).join(', ')}
                  </p>
                )}

                {unreadable.length > 0 && (
                  <p className="mt-1 text-xs text-amber-300">
                    Model could not read: {unreadable.join(', ')}
                  </p>
                )}

                {c.errorMessage && <p className="mt-1 text-xs text-red-300">{c.errorMessage}</p>}

                {!readOnly && (c.status === 'extracted' || c.status === 'failed') && (
                  <Button
                    size="sm"
                    variant="secondary"
                    className="mt-2"
                    icon={<RotateCcw size={13} />}
                    onClick={() => reopen(c)}
                  >
                    {c.status === 'failed' ? 'Enter by hand' : 'Confirm now'}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Modal>
  );
}
